import {datadogRum} from '@datadog/browser-rum';

import useTracking from '@/hooks/useTracking';
import {gtm} from '@/utils';

// `NAVLINK_VERSIONS` now lives in `../appBarTracking` alongside the other AppBar
// tracking constants; re-exported here for back-compat with existing importers.
export {NAVLINK_VERSIONS} from '../appBarTracking';

export type TrackLinkArgs = {
  /** Desired tracking event path. */
  path: string;
  /** Text of the link that was clicked. */
  title: string;
  /** Optional version tag. */
  version?: string;
  /** Optional action name for Datadog RUM. */
  datadogAction?: string;
};

export type TrackRumActionArgs = {
  /** Datadog RUM action name. */
  datadogAction: string;
  /** Optional version tag (top-level RUM attribute, per experiment arm). */
  version?: string;
};

const useNavLinkTracking = () => {
  const {track} = useTracking();

  /**
   * Tracks a click on a navigation link,
   * sending events to tracking and observability platforms.
   */
  const trackLink = ({path, title, version, datadogAction}: TrackLinkArgs) => {
    track('navlink-clicked', {
      url: window.location.href,
      misc_json: JSON.stringify({
        item_clicked: {link: path, text: title},
        ...(version && {version}),
      }),
    });

    gtm.dataLayer({
      event: 'track_event_header_nav',
      event_category: 'Navigation',
      event_action: 'Header',
      event_label: title,
      event_value: 0,
      non_interaction: false,
    });

    if (datadogAction) {
      datadogRum.addAction(datadogAction, {
        ...(version && {version}),
      });
    }
  };

  /**
   * Fires a standalone Datadog RUM action (no tracking / GTM event).
   * Used for hover and click observability that only needs RUM coverage.
   * `version` mirrors the existing navlink.*.clicked convention (top-level attribute).
   */
  const trackRumAction = ({datadogAction, version}: TrackRumActionArgs) => {
    datadogRum.addAction(datadogAction, {
      ...(version && {version}),
    });
  };

  return {trackLink, trackRumAction};
};

export default useNavLinkTracking;
